import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Building2,
  Calendar,
  CreditCard,
  Star,
  MessageSquare,
  Clock,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Mail,
  Landmark,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LucideIcon } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { ConnectedLanguageSwitcher } from './LanguageSwitcher'
import { useReceivedInvitationsPendingCount } from '../hooks/useCoOwnershipInvitations'
import { useActiveSupportCaseCount } from '../hooks/useSupportCases'
import { ActiveConflictIndicator } from './ActiveConflictIndicator'

interface NavItem {
  name: string;
  to: string;
  icon: LucideIcon;
  badgeCount?: number;
}

const Layout = () => {
  const { logout } = useAuthStore()
  const { t } = useTranslation('navigation')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const pendingInvitationCount = useReceivedInvitationsPendingCount()
  const { count: activeSupportCaseCount } = useActiveSupportCaseCount()

  const navigation: NavItem[] = [
    { name: t('dashboard'), to: '/dashboard', icon: LayoutDashboard },
    { name: t('apartments'), to: '/apartments', icon: Building2 },
    { name: t('bookings'), to: '/bookings', icon: Calendar },
    { name: t('payments'), to: '/payments', icon: CreditCard },
    { name: t('reviews'), to: '/reviews', icon: Star },
    { name: t('inquiries'), to: '/inquiries', icon: MessageSquare },
    { name: t('deferredBookings'), to: '/deferred-bookings', icon: Clock },
    { name: t('supportCases'), to: '/support-cases', icon: MessageSquare, badgeCount: activeSupportCaseCount },
    { name: t('bankTransfers'), to: '/bank-transfers', icon: Landmark },
    { name: t('content'), to: '/content', icon: FileText },
    { name: t('myInvitations'), to: '/my-invitations', icon: Mail, badgeCount: pendingInvitationCount },
    { name: t('settings'), to: '/settings', icon: Settings },
  ]

  const closeSidebar = () => setSidebarOpen(false)

  const sidebarContent = (
    <>
      <div className="p-6 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-primary-500">{t('adminPanel')}</h1>
        <p className="text-sm text-secondary-500 mt-1">{t('rentApartment')}</p>
      </div>

      <ActiveConflictIndicator />

      <nav className="flex-1 p-4">
        {navigation.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200 ${
                isActive
                  ? 'bg-primary-50 text-primary-600 font-semibold shadow-sm'
                  : 'text-secondary-700 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <item.icon size={20} />
            <span className="flex-1">{item.name}</span>
            {(item.badgeCount ?? 0) > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-semibold bg-primary-600 text-white rounded-full">
                {item.badgeCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <ConnectedLanguageSwitcher />

      <div className="p-4 border-t border-gray-100">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-secondary-700 hover:bg-gray-50 hover:text-gray-900 w-full transition-all duration-200"
        >
          <LogOut size={20} />
          <span>{t('logout')}</span>
        </button>
      </div>
    </>
  )

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar — hidden on mobile */}
      <aside className="hidden md:flex w-64 bg-white border-r border-gray-100 flex-col shadow-sm">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          aria-hidden="true"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-100 flex flex-col shadow-xl z-30 transition-transform duration-300 md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label={t('adminPanel')}
      >
        {/* Drawer close button */}
        <button
          onClick={closeSidebar}
          aria-label={t('closeSidebar')}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-gray-50">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label={t('openSidebar')}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Menu size={24} />
          </button>
          <span className="text-lg font-bold text-primary-500">{t('adminPanel')}</span>
        </div>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default Layout
